import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import AssignmentTurnedInRounded from "@mui/icons-material/AssignmentTurnedInRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import GroupRounded from "@mui/icons-material/GroupRounded";
import PercentRounded from "@mui/icons-material/PercentRounded";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import {
  CHART_HEIGHT,
  ControlBar,
  PanelHeader,
  PanelLoader,
  StatCard,
  UsagePanel,
  formatDayLabel,
} from "components/dashboard/usage/UsagePanels";
import { AuthContext } from "context/Auth";
import {
  completionRate,
  currentYearMonth,
  fetchOrgDailyActivity,
  fetchOrgUserActivity,
} from "services/orgUsageService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const RANGE_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

/**
 * UsagePage — what an organization's ChartMind usage actually looks like.
 *
 * This replaces a monthly token-capacity bar that could never move: it read
 * `organization.tokensUsedThisMonth`, a field only the older chatbot path writes, against a
 * hardcoded 500K-tokens-per-seat limit that nothing enforces. A ChartMind-only organization
 * saw 0% of a fictional allowance, forever.
 *
 * Encounters and clinicians rather than dollars, deliberately. Tokens are not a unit a clinic
 * manager can act on, and per-encounter cost is our margin — it belongs on the platform admin
 * page unless the organization is billed on usage.
 */
const UsagePage = () => {
  const { organization, organizationMembers, userData } = useContext(AuthContext);
  const theme = useTheme();

  const organizationId = organization?.id || userData?.organizationId || null;

  // Team size, not seats. A seat count is a billing artefact: organizations on a beta or
  // internal arrangement have none, so the old chip read "0 seats" to exactly the people least
  // able to do anything about it, and "3 of 0 seats" underneath the clinician count. How much
  // of the team is actually using ChartMind is the question a clinic manager has either way.
  const teamSize = (organizationMembers || []).length;

  const [rangeDays, setRangeDays] = useState(30);
  const [activity, setActivity] = useState(null);
  const [userRows, setUserRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const yearMonth = useMemo(() => currentYearMonth(), []);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    // Both reads are served from the module cache, so navigating away and back does not
    // re-read a document per day plus the whole user-stats collection every time.
    Promise.all([
      fetchOrgDailyActivity(organizationId, rangeDays),
      fetchOrgUserActivity(organizationId, yearMonth),
    ])
      .then(([daily, users]) => {
        if (cancelled) return;
        setActivity(daily);
        setUserRows(users);
      })
      .catch((err) => {
        console.error("Failed to load organization usage:", err);
        if (!cancelled) setError("Failed to load usage data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, rangeDays, yearMonth]);

  const totals = activity?.totals;
  const rate = completionRate(totals);
  const activeClinicians = userRows.length;

  const memberName = useMemo(() => {
    const byId = {};
    (organizationMembers || []).forEach((m) => {
      byId[m.id || m.uid] = m.fullName || m.name || m.email || m.id || m.uid;
    });
    return (userId) => byId[userId] || "Unknown member";
  }, [organizationMembers]);

  /**
   * One bar per day, stacked — not two bars side by side.
   *
   * Charted encounters are a subset of started ones, not a parallel quantity: every chart came
   * from an encounter that was started. Side-by-side bars invited reading them as two separate
   * volumes, and made the gap between them (the encounters that stalled) something you had to
   * eyeball rather than something the chart drew.
   *
   * The remainder is clamped at zero because the two counters are not closed over a single day:
   * an encounter started before midnight and charted after it lands its steps in different
   * documents, so charted can exceed started on a given day. Charted is shown as recorded
   * rather than capped — completed work should not disappear to make the funnel tidy.
   */
  const chartData = useMemo(() => {
    if (!activity) return { labels: [], datasets: [] };
    return {
      labels: activity.dailyRows.map((row) => formatDayLabel(row.dateKey)),
      datasets: [
        {
          label: "Completed",
          data: activity.dailyRows.map((row) => row.charted),
          backgroundColor: theme.palette.success.main,
          stack: "encounters",
        },
        {
          // Primary, not grey. These are encounters a clinician actually ran; they are work,
          // not absent data, and greying them out said the opposite. The colours also match
          // the cards above: started is primary, completed is success.
          label: "Incomplete",
          data: activity.dailyRows.map((row) => Math.max(row.started - row.charted, 0)),
          backgroundColor: theme.palette.primary.main,
          stack: "encounters",
        },
      ],
    };
  }, [activity, theme]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top", labels: { color: theme.palette.text.secondary } },
        tooltip: {
          callbacks: {
            // The stack total is the day's encounters, which is the number being read off the
            // bar height — so say it, rather than leaving it to be added up by eye.
            footer: (items) =>
              `Encounters: ${items.reduce((sum, i) => sum + i.parsed.y, 0).toLocaleString()}`,
          },
        },
      },
      // Axis text and grid from the theme: Chart.js defaults assume a light background.
      scales: {
        x: {
          stacked: true,
          ticks: { color: theme.palette.text.secondary },
          grid: { color: theme.palette.divider },
          border: { color: theme.palette.divider },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: { precision: 0, color: theme.palette.text.secondary },
          grid: { color: theme.palette.divider },
          border: { color: theme.palette.divider },
        },
      },
    }),
    [theme]
  );

  if (!organizationId) {
    return (
      <Box sx={{ mb: 4 }}>
        <DashboardPageHeader
          title="Usage"
          subtitle="ChartMind activity across your team."
        />
        <Alert severity="info">
          This account is not linked to an organization, so there is no team usage to show.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <DashboardPageHeader
        title="Usage"
        subtitle="ChartMind activity across your team."
      />

      {/* Period on the left, as on the platform usage page. This page has no view options, so
          the right-hand cluster of that bar is simply absent rather than differently placed. */}
      <ControlBar>
        <ToggleButtonGroup
          value={rangeDays}
          exclusive
          onChange={(_, v) => v && setRangeDays(v)}
          size="small"
        >
          {RANGE_OPTIONS.map((o) => (
            <ToggleButton key={o.value} value={o.value} sx={{ px: 1.5 }}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </ControlBar>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <PanelLoader />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 4, alignItems: "stretch" }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Encounters started"
                value={(totals?.started || 0).toLocaleString()}
                subtitle={`Last ${rangeDays} days`}
                icon={<AssignmentTurnedInRounded />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Charts completed"
                value={(totals?.charted || 0).toLocaleString()}
                subtitle={`Last ${rangeDays} days`}
                icon={<DescriptionRounded />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Completion rate"
                value={rate === null ? "—" : `${Math.round(rate * 100)}%`}
                subtitle="Started encounters that reached a chart"
                icon={<PercentRounded />}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active clinicians"
                value={activeClinicians.toLocaleString()}
                subtitle={
                  teamSize > 0
                    ? `of ${teamSize} team member${teamSize === 1 ? "" : "s"}, this month`
                    : "This month"
                }
                icon={<GroupRounded />}
                color="warning"
              />
            </Grid>
          </Grid>

          <UsagePanel sx={{ mb: 4 }}>
            <PanelHeader overline="Daily encounters" />
            <Box sx={{ height: CHART_HEIGHT }}>
              <Bar data={chartData} options={chartOptions} />
            </Box>
          </UsagePanel>

          <UsagePanel>
            <PanelHeader
              overline="By clinician"
              note="This calendar month. Team members who have not used ChartMind this month are not listed."
            />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Clinician</TableCell>
                    <TableCell align="right">Encounters started</TableCell>
                    <TableCell align="right">Charts completed</TableCell>
                    <TableCell align="right">Completion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userRows.map((row) => (
                    <TableRow key={row.userId}>
                      <TableCell>{memberName(row.userId)}</TableCell>
                      <TableCell align="right">{row.started.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.charted.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {row.started
                            ? `${Math.round((row.charted / row.started) * 100)}%`
                            : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!userRows.length && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No ChartMind activity from your team this month.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </UsagePanel>
        </>
      )}
    </Box>
  );
};

export default UsagePage;
